import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Skeleton } from "../ui/skeleton";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { formatCurrency, formatDate } from "../../lib/utils";
import { useTaxConfig, computeGst } from "../../lib/tax";

export function RenewDialog({ open, onOpenChange, subscriptionId, onDone }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, preview: null });
  const [force, setForce] = useState(false);
  const [saving, setSaving] = useState(false);
  const taxCfg = useTaxConfig();

  const loadPreview = useCallback(async () => {
    setState({ loading: true, error: null, preview: null });
    try {
      const { data } = await apiClient.get(`/subscriptions/${subscriptionId}/renew-preview`);
      setState({ loading: false, error: null, preview: data });
    } catch (error) {
      setState({ loading: false, error: getApiErrorMessage(error, "Could not load the renewal preview."), preview: null });
    }
  }, [subscriptionId]);

  useEffect(() => {
    if (open) { setForce(false); loadPreview(); }
  }, [open, loadPreview]);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.post(`/subscriptions/${subscriptionId}/renew`, null, { params: { force } });
      toast.success(`Subscription renewed — new subscription #${data.id} created`, {
        action: { label: "View", onClick: () => navigate(`/subscriptions/${data.id}`) },
      });
      onDone();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not renew this subscription."));
    } finally {
      setSaving(false);
    }
  };

  const p = state.preview;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="subscription-renew-dialog">
        <DialogHeader>
          <DialogTitle>Renew this subscription?</DialogTitle>
          <DialogDescription>
            Creates a brand new Active subscription for the same customer and plan, with a fresh pending payment. This one is left untouched.
          </DialogDescription>
        </DialogHeader>

        {state.loading ? (
          <Skeleton className="h-32 w-full" />
        ) : state.error ? (
          <p className="text-sm text-danger" data-testid="subscription-renew-error">{state.error}</p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3 rounded-md border border-border bg-background p-3 text-sm">
              <div><p className="text-xs text-text-muted">Plan</p><p className="font-medium text-text-main">{p.plan_name || `#${p.plan_id}`}</p></div>
              <div><p className="text-xs text-text-muted">New Start</p><p className="font-medium text-text-main">{formatDate(p.new_start_date)}</p></div>
              <div><p className="text-xs text-text-muted">New End</p><p className="font-medium text-text-main">{formatDate(p.new_end_date)}</p></div>
              <div><p className="text-xs text-text-muted">Subtotal</p><p className="font-medium text-text-main">{formatCurrency(p.price)}</p></div>
              <div><p className="text-xs text-text-muted">GST ({computeGst(p.price, taxCfg).rate}%)</p><p className="font-medium text-text-main">{formatCurrency(computeGst(p.price, taxCfg).gst)}</p></div>
              <div><p className="text-xs text-text-muted">Total</p><p className="font-semibold text-text-main" data-testid="subscription-renew-total">{formatCurrency(computeGst(p.price, taxCfg).total)}</p></div>
              <div><p className="text-xs text-text-muted">Addons</p><p className="font-medium text-text-main">{p.addon_ids.length}</p></div>
              <div><p className="text-xs text-text-muted">Delivery Days</p><p className="font-medium text-text-main">{p.deliver_days.length}</p></div>
            </div>
            {p.duplicate_renewal_exists && (
              <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning-bg p-3 text-sm text-warning" data-testid="subscription-renew-duplicate-warning">
                <AlertTriangle size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">A renewal already exists today (subscription #{p.duplicate_subscription_id}).</p>
                  <label className="mt-2 flex items-center gap-2 text-text-main">
                    <Checkbox checked={force} onCheckedChange={(v) => setForce(Boolean(v))} data-testid="subscription-renew-force-checkbox" />
                    Renew anyway (create a duplicate)
                  </label>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="subscription-renew-cancel-button">
            Cancel
          </Button>
          <Button
            variant="accent"
            disabled={saving || state.loading || !!state.error || (p?.duplicate_renewal_exists && !force)}
            onClick={handleConfirm}
            data-testid="subscription-renew-confirm-button"
          >
            {saving ? "Renewing..." : "Confirm Renewal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
